import React from 'react';
import { WarningAmber } from '@mui/icons-material';
import { Button } from '@mui/material';

import Donation from 'entities/donation/model';
import { CurrencyCode, currencyFormat } from 'utils/currencyFormat';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { formatDateWithoutTimezone } from './utils';

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <strong style={{ fontWeight: 500 }}>{children}</strong>
);

export const AiFeedback = ({ donation }: { donation: Donation }) => {
  if (!donation.donationReceipt?.reasoningNote) {
    return null;
  }

  const userReceiptDataOverrides = donation.donationReceipt.userReceiptDataOverrides || {};
  const parsedReceiptData = donation.donationReceipt.parsedReceiptData || {};

  if (Object.keys(userReceiptDataOverrides).filter((key) => key !== '_id').length > 0) {
    return (
      <>
        <p>
          <WarningAmber style={{ color: '#BE5602', marginRight: 4 }} />
          There are discrepancies detected between the receipt and the input form:
        </p>
        <ul style={{ paddingLeft: 12 }}>
          {'amount' in userReceiptDataOverrides && (
            <li>
              The donation amount entered (
              <Highlight>
                {currencyFormat(Number(userReceiptDataOverrides.amount), donation.currency)}
              </Highlight>
              ) does not match the amount on the receipt (
              <Highlight>
                {currencyFormat(
                  Number(parsedReceiptData.amount),
                  parsedReceiptData.currencyCode as CurrencyCode
                )}
              </Highlight>
              )
            </li>
          )}
          {'userName' in userReceiptDataOverrides &&
            (parsedReceiptData.userName ? (
              <li>
                The donor name entered (<Highlight>{userReceiptDataOverrides.userName}</Highlight>)
                does not match the name on the receipt (
                <Highlight>{parsedReceiptData.userName}</Highlight>)
              </li>
            ) : (
              <li>The donor name and surname are missing on the receipt.</li>
            ))}
          {'nonprofitName' in userReceiptDataOverrides && (
            <li>
              The nonprofit name entered (
              <Highlight>{userReceiptDataOverrides.nonprofitName}</Highlight>) does not match the
              name on the receipt (<Highlight>{parsedReceiptData.nonprofitName}</Highlight>)
            </li>
          )}
          {'date' in userReceiptDataOverrides && (
            <li>
              The donation date entered (
              <Highlight>
                {formatDateWithoutTimezone(userReceiptDataOverrides.date as Date)}
              </Highlight>
              ) does not match the date on the receipt (
              <Highlight>{formatDateWithoutTimezone(parsedReceiptData.date as Date)}</Highlight>)
            </li>
          )}
          {'externalDonationType' in userReceiptDataOverrides && (
            <li>
              The donation type entered (
              <Highlight>{userReceiptDataOverrides.externalDonationType}</Highlight>) does not match
              the type on the receipt (
              <Highlight>{parsedReceiptData.externalDonationType}</Highlight>)
            </li>
          )}
        </ul>
        <p>This may require further review.</p>

        <div style={{ textAlign: 'right' }}>
          <Tooltip title={<>{donation.donationReceipt.reasoningNote}</>}>
            <Button variant="text">Additional details</Button>
          </Tooltip>
        </div>
      </>
    );
  }

  return <p>{donation.donationReceipt.reasoningNote}</p>;
};
